import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { FormFieldDescriptor, ResolvedFormOption } from '@finance/contracts';
import type { StaticTranslator } from '../../i18n/static-translator';
import { resolveFieldOptions, toResolvedOptions, type OptionResolverContext } from './option-resolution';

interface UseFieldOptionsResult {
  options: ResolvedFormOption[];
  loading: boolean;
  error: string | null;
  empty: boolean;
  reload: () => void;
}

const NO_QUERY_DEPENDENCIES: readonly string[] = Object.freeze([]);

export function useFieldOptions(
  field: FormFieldDescriptor,
  values: Record<string, unknown>,
  t: StaticTranslator,
  resolverCtx?: OptionResolverContext,
): UseFieldOptionsResult {
  const staticOptions = useMemo(
    () => (field.options && field.options.length > 0 ? toResolvedOptions(field.options, t) : null),
    [field.options, t],
  );
  const hasAsyncSource = !staticOptions && !!field.optionSource;
  const [options, setOptions] = useState<ResolvedFormOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [empty, setEmpty] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  const reload = useCallback(() => setReloadTrigger((n) => n + 1), []);

  const optionSourceKey = useMemo(() => {
    const src = field.optionSource;
    if (!src) return field.options ? '__static__' : '__none__';
    const deps = src.queryDependencies ?? [];
    const params = src.queryParams ? JSON.stringify(src.queryParams) : '';
    return `${src.adapterId as string}|${src.valuePath}|${src.labelPath}|${deps.join(',')}|${params}`;
  }, [field]);

  const deps =
    (field.optionSource?.queryDependencies as readonly string[] | undefined) ??
    NO_QUERY_DEPENDENCIES;
  const depValues = useMemo(() => deps.map((d) => values[d as string]).join('|'), [deps, values]);

  const fieldRef = useRef(field);
  const valuesRef = useRef(values);
  fieldRef.current = field;
  valuesRef.current = values;

  useEffect(() => {
    if (!hasAsyncSource) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    setEmpty(false);

    const currentField = fieldRef.current;
    const currentValues = valuesRef.current;
    const queryDependencies = currentField.optionSource?.queryDependencies ?? [];
    const queryValues: Record<string, unknown> = {};
    for (const dep of queryDependencies) queryValues[dep as string] = currentValues[dep as string];

    resolveFieldOptions(currentField, queryValues, resolverCtx)
      .then((resolved) => {
        if (controller.signal.aborted) return;
        setOptions(toResolvedOptions(resolved, t));
        setLoading(false);
        setEmpty(resolved.length === 0);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : t('form:optionsLoadError'));
        setLoading(false);
        setOptions([]);
        setEmpty(true);
      });

    return () => controller.abort();
    // `field` and `values` are read via refs; the effect should run only when
    // the option source fingerprint or the relevant query values change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAsyncSource, optionSourceKey, depValues, reloadTrigger, resolverCtx, t]);

  if (staticOptions) {
    return { options: staticOptions, loading: false, error: null, empty: staticOptions.length === 0, reload };
  }

  if (!field.optionSource) {
    return { options: [], loading: false, error: null, empty: true, reload };
  }

  return { options, loading, error, empty, reload };
}
